// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { type ApiErrorPayloadCommon, isApiError } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import { apiClient } from '../client';

const createAuthorizeDeviceHandoffRequest = async (
  handoffId: string,
  codingContext: CodingContext | undefined,
): Promise<void> => {
  try {
    await apiClient.post<void>('handoff/authorize', () => handoffId, codingContext);
  } catch (error) {
    console.error('Error authorizing device handoff:', error);
  }
};

export const useAuthorizeDeviceHandoffMutation = (options: {
  getHandoffId: () => string;
  onSuccess: () => void;
}) => {
  const { codingContext } = usePlatform();
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | undefined>(undefined);
  const mutation = useMutation({
    mutationFn: () => createAuthorizeDeviceHandoffRequest(options.getHandoffId(), codingContext),
    onSuccess: () => {
      options.onSuccess();
    },
    onError(error) {
      if (isApiError(error)) {
        setApiError(error);
      } else {
        setApiError({ error: { code: 'unknown', message: 'Unknown error' } });
      }
    },
  });

  return {
    authorizeDeviceHandoff: mutation.mutate,
    apiError,
    ...mutation,
  };
};
